import { useEffect, useState } from "react";
import api from "@/utils/axios";
import cls from "../financePage.module.scss";

const PromoCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    code: "",
    amountNoty: "",
    active: true,
    expiresAt: "",
    usageLimit: "",
  });

  const fetchCodes = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/v1/admin/promos");
      setCodes(res.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load promo codes",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  const handleChange = (field) => (event) => {
    const value =
      field === "active" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      code: "",
      amountNoty: "",
      active: true,
      expiresAt: "",
      usageLimit: "",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setError("");
      await api.post("/v1/admin/promos", {
        code: form.code,
        amountNoty: Number(form.amountNoty),
        active: form.active,
        expiresAt: form.expiresAt || null,
        usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
      });
      resetForm();
      await fetchCodes();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to create promo code",
      );
    }
  };

  const toggleActive = async (promo) => {
    try {
      const res = await api.patch(`/v1/admin/promos/${promo.id}/active`, {
        active: !promo.active,
      });
      setCodes((prev) =>
        prev.map((item) =>
          item.id === promo.id ? { ...item, active: res.data.active } : item,
        ),
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update promo",
      );
    }
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerLeft}>
          <p className={cls.eyebrow}>Finance • Promo codes</p>
          <h1 className={cls.title}>Promo codes</h1>
          <p className={cls.subtitle}>
            Create and monitor promo codes and redemption limits.
          </p>
        </div>
      </header>

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>Create promo</h2>
        <form className={cls.filters} onSubmit={handleSubmit}>
          <label className={cls.formRow}>
            <span className={cls.label}>Code</span>
            <input
              className={cls.input}
              value={form.code}
              onChange={handleChange("code")}
              placeholder="WELCOME10"
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Amount (Noty)</span>
            <input
              className={cls.input}
              type="number"
              min="0"
              step="1"
              value={form.amountNoty}
              onChange={handleChange("amountNoty")}
              placeholder="100"
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Usage limit</span>
            <input
              className={cls.input}
              type="number"
              min="0"
              step="1"
              value={form.usageLimit}
              onChange={handleChange("usageLimit")}
              placeholder="optional"
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Expires</span>
            <input
              className={cls.input}
              type="date"
              value={form.expiresAt}
              onChange={handleChange("expiresAt")}
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Active</span>
            <input
              type="checkbox"
              checked={form.active}
              onChange={handleChange("active")}
            />
          </label>
          <div className={cls.inlineActions}>
            <button className={`${cls.btn} ${cls.btnPrimary}`} type="submit">
              Create promo
            </button>
            <button
              className={`${cls.btn} ${cls.btnSecondary}`}
              type="button"
              onClick={resetForm}
            >
              Reset
            </button>
          </div>
        </form>
      </section>

      {error && (
        <div className={`${cls.stateBox} ${cls.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      )}

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>Promo list</h2>
        {loading ? (
          <div className={cls.stateBox}>Loading promo codes…</div>
        ) : codes.length === 0 ? (
          <div className={cls.stateBox}>No promo codes found.</div>
        ) : (
          <div className={cls.tableWrap}>
            <table className={cls.table}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Usage</th>
                  <th>Redemptions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((promo) => (
                  <tr key={promo.id}>
                    <td className={cls.code}>{promo.code}</td>
                    <td>{promo.amountNoty}</td>
                    <td>
                      <span className={cls.pill}>
                        {promo.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      {promo.usedCount ?? 0}
                      {promo.usageLimit ? ` / ${promo.usageLimit}` : ""}
                    </td>
                    <td>{promo.redemptionCount ?? 0}</td>
                    <td>
                      <div className={cls.inlineActions}>
                        <button
                          type="button"
                          className={cls.btn}
                          onClick={() => toggleActive(promo)}
                        >
                          {promo.active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default PromoCodes;
